import Link from 'next/link';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useTranslation } from 'react-i18next';

import { HELP_CENTER_URL } from '@highlight-cp/constants';
import { Button, Stack, Text, Title } from '@highlight-cp/core-ui';
import { ExternalLink } from '@highlight-cp/core-ui-compositions';
import AuthUIWrapper from 'app/components/AuthUIWrapper';
import SignedInNotFound from 'app/components/SignedInNotFound';
import { useOptionalClientPortalAuth } from '../../../auth/ClientPortalAuthProvider';

interface SignedOutNotFoundProps {
  // Whether we know the visitor is signed out. Static error pages can't tell, so they link to
  // `/`, which sends signed-in visitors home and everyone else to sign in.
  knownSignedOut: boolean;
}

const SignedOutNotFound = ({ knownSignedOut }: SignedOutNotFoundProps) => {
  const { t } = useTranslation();

  return (
    <AuthUIWrapper showTerms={false}>
      <Stack align="stretch" gap="md">
        <Title order={2} ta="center">
          {t('Page not found')}
        </Title>
        <Text size="sm" c="dimmed" ta="center">
          {knownSignedOut
            ? t("This page doesn't exist or has moved. Sign in to get back to your projects.")
            : t("This page doesn't exist or has moved.")}
        </Text>
        {knownSignedOut ? (
          <Button component={Link} href="/auth/login" fullWidth>
            {t('Sign in')}
          </Button>
        ) : (
          <Button component={Link} href="/" fullWidth>
            {t('Go to Highlight')}
          </Button>
        )}
        <Text size="sm" c="dimmed" ta="center">
          {t('Need a hand?')}{' '}
          <ExternalLink href={HELP_CENTER_URL}>{t('Visit our help center')}</ExternalLink>
        </Text>
      </Stack>
    </AuthUIWrapper>
  );
};

const NotFound = () => {
  const { t } = useTranslation();
  const router = useRouter();
  // Never 'loading' here: the catch-all renders inside AuthGuard, which shows its own loader until
  // the session settles, and static error pages have no auth state at all.
  const auth = useOptionalClientPortalAuth();

  return (
    <>
      <NextSeo title={t('Page not found')} noindex />
      {auth?.status === 'authenticated' ? (
        // Path only: a mistyped link can carry a token in its query or hash.
        <SignedInNotFound missingPath={router.asPath.split(/[?#]/)[0] ?? ''} />
      ) : (
        <SignedOutNotFound knownSignedOut={auth !== null} />
      )}
    </>
  );
};

export default NotFound;
